import React, { useState } from 'react';
import {
  Card,
  Select,
  Form,
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Collapse,
  Typography,
  Space,
  Switch,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import CorporateTraderService from '../../services/CorporateTraderService';
import WorkflowAccOpeningService from '../../services/WorkflowAccOpeningService';

const { Panel } = Collapse;
const { Title } = Typography;

export default function PrepareCorporateTrader() {
  // React hooks
  const navigate = useNavigate();
  const { showNotification } = useAuth();
  const [loading, setLoading] = useState(false);

  // Input options
  const clientType = ['LOCAL_CORPORATE', 'FOREIGN_CORPORATE'];
  const netWorthOptions = [
    'Below & Equal to MMK 10,000,000',
    'From MMK 10,000,001 to MMK 50,000,000',
    'From MMK 50,000,001 to MMK 100,000,000',
    'Above MMK 100,000,000',
  ];
  const sourceOfFundOptions = ['EMPLOYMENT', 'SELF_EMPLOYED', 'INHERITANCE', 'OTHERS'];

  // Form related setting
  const [form] = Form.useForm();
  const [requiredMark, setRequiredMarkType] = useState('');
  const onRequiredTypeChange = ({ requiredMarkValue }) => {
    setRequiredMarkType(requiredMarkValue);
  };

  // Form
  const onFinish = async formValues => {
    console.log('clicked');
    const payload = {
      agreementDate: new Date(formValues.agreementDate.valueOf()),
      exchangeSegment: formValues.exchangeSegment,
      applicationNo: formValues.applicationNo ? formValues.applicationNo : null,
      primaryEmail: formValues.primaryEmail,
      corporateProfile: {
        firmName: formValues.traderFirmName,
        registrationNo: formValues.traderRegistrationNo,
        isForeign: formValues.traderClientType,
        dateOfIncorporation: formValues.traderDateOfIncorporation,
        sourceOfFund: formValues.traderSourceOfFund,
        netWorth: formValues.traderNetWorth,
        netWorthOnDate: new Date(formValues.traderNetWorthOnDate.valueOf()),
        latestFinancialYearDatedAs: formValues.traderLatestFinancialYearDate,
        boardOfResolutionDatedAs: formValues.traderBoardOfResolutionDate,
        inPersonVerification: formValues.traderInPersonVerification,
        typeOfinstitution: formValues.traderTypeOfInstitution,
        typeOfFacility: formValues.traderTypeOfFacility,
        paidUpCapital: formValues.traderPaidUpCapital,
        description: formValues.traderDescription,
      },
    };

    console.log(payload);
    setLoading(true);
    const result = await WorkflowAccOpeningService.prepareCorporateAcc(payload);

    // console.log(result.data.traderId);
    setLoading(false);
    if (result.success) {
      showNotification('success', 'Successfully submitted');
      navigate(`/corporate/fill/${result.data.workflowId}`);
    } else {
      showNotification('error', result.message);
    }
  };

  return (
    <>
      <Title level={4} style={{ marginTop: 0, marginBottom: '2rem' }}>
        Corporate Account Opening - Submission
      </Title>
      <Card title="Step 1 of 2: Peparation" style={{ background: '#fcfcfc' }}>
        <Form
          onFinish={onFinish}
          form={form}
          labelCol={{
            span: 8,
            lg: {
              span: 14,
            },
            xl: {
              span: 12,
            },
          }}
          layout="horizontal"
          size={'medium'}
          initialValues={{
            requiredMarkValue: requiredMark,
            exchangeSegment: 'YSX',
            traderClientType: false,
            traderSourceOfFund: 'EMPLOYMENT',
            traderNetWorth: 'Below & Equal to MMK 10,000,000',
            traderInPersonVerification: false,
          }}
          onValuesChange={onRequiredTypeChange}
          requiredMark={requiredMark}
        >
          <Collapse defaultActiveKey={['1']} bordered={false} expandIconPosition={'end'} accordion>
            {/* Application Information */}
            <Panel header={'Application Information'} key={'1'}>
              <Row gutter={16} style={{ marginTop: '2rem' }}>
                <Col xs={24} sm={24} md={24} lg={12}>
                  <Form.Item
                    label="Exchange Segment"
                    name={'exchangeSegment'}
                    rules={[
                      {
                        required: true,
                        message: 'Please select',
                      },
                    ]}
                  >
                    <Select>
                      <Select.Option value="YSX">YSX</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    label="Primary Email"
                    name={'primaryEmail'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                        type: 'email',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={12}>
                  <Form.Item
                    label="Agreement Date"
                    name={'agreementDate'}
                    rules={[
                      {
                        required: true,
                        message: 'Please select agreement date',
                      },
                    ]}
                  >
                    <DatePicker
                      style={{
                        width: '100%',
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>

            {/* Trader Profile */}
            <Panel header={'Trader Profile'} key={'2'}>
              <Row gutter={16} style={{ marginTop: '2rem' }}>
                <Col
                  xs={{
                    span: 24,
                    // order: 2,
                  }}
                  sm={24}
                  md={24}
                  lg={{
                    span: 12,
                    // order: 1,
                  }}
                >
                  <Form.Item
                    label="Firm Name"
                    name={'traderFirmName'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label="Registration No"
                    name={'traderRegistrationNo'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label="Client Type"
                    name={'traderClientType'}
                    initialValue={false}
                    rules={[
                      {
                        required: true,
                        message: 'Please select',
                      },
                    ]}
                  >
                    <Select
                      // defaultValue={false}
                      style={{
                        width: '100%',
                      }}
                      options={[
                        {
                          value: false,
                          label: 'LOCAL_CORPORATE',
                        },
                        {
                          value: true,
                          label: 'FOREIGN_CORPORATE',
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Date of Incorporation"
                    name={'traderDateOfIncorporation'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <DatePicker
                      style={{
                        width: '100%',
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Source Of Fund"
                    name={'traderSourceOfFund'}
                    rules={[
                      {
                        required: true,
                        message: 'Please select',
                      },
                    ]}
                  >
                    <Select
                      options={sourceOfFundOptions.map(item => ({
                        labe: item,
                        value: item,
                      }))}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Net Worth"
                    name={'traderNetWorth'}
                    rules={[
                      {
                        required: true,
                        message: 'Please select',
                      },
                    ]}
                  >
                    <Select
                      options={netWorthOptions.map(item => ({
                        label: item,
                        value: item,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Net Worth on Date"
                    name={'traderNetWorthOnDate'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <DatePicker
                      style={{
                        width: '100%',
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={{
                    span: 24,
                    // order: 1,
                  }}
                  sm={24}
                  md={24}
                  lg={{
                    span: 12,
                    // order: 2,
                  }}
                >
                  <Form.Item
                    label="Latest Financial Year Date"
                    name={'traderLatestFinancialYearDate'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <DatePicker
                      style={{
                        width: '100%',
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Board Of Resolution Date"
                    name={'traderBoardOfResolutionDate'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <DatePicker
                      style={{
                        width: '100%',
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Type Of Institution"
                    name={'traderTypeOfInstitution'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label="Type Of Facility"
                    name={'traderTypeOfFacility'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label="Paid Up Capital"
                    name={'traderPaidUpCapital'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <Input style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    label="Description"
                    name={'traderDescription'}
                    rules={[
                      {
                        required: true,
                        message: 'Please do not leave blank',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label="Status (In Person Verification)"
                    name="traderInPersonVerification"
                    valuePropName="checked"
                    // initialValue={false}
                  >
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>

          <Space
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItem: 'center',
              marginTop: '2rem',
            }}
          >
            <Button type="default" size={'large'} onClick={() => navigate('/')}>
              <ArrowLeftOutlined /> Back
            </Button>
            <Button type="primary" key="submit" htmlType="submit" size={'large'} loading={loading}>
              Prepare
            </Button>
          </Space>
        </Form>
      </Card>
    </>
  );
}
